import { Injectable } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';
import { CustType } from './customer.service';
import { ErrorMessageResponse } from '@app/core/models/data-response';

@Injectable({
    providedIn: 'root',
})
export class CustactService {
    constructor(private datatask: DataTaskService) {}

    /**
     * Retrieves custact.
     *
     * @datatask 48
     * @datataskName sp_ws_p2_custact_get
     * @param params custact no
     * @returns promise containing the custact
     */
    public async getCustact(params: GetCustactParams): Promise<CustactGet> {
        return (await this.datatask.Post(28, params))[0];
    }

    /**
     * Deletes the given custact.
     *
     * @datatask 136
     * @datataskName sp_ws_p2_custact_delete
     * @param params the custact to delete.
     * @returns promise being either ok '1' if save went through or undefined if not (validation dialog automatically pops up).
     */
    public async deleteCustact(params: GetCustactParams): Promise<{ ok: '1' } | undefined> {
        return (await this.datatask.Post(136, params))[0];
    }

    /**
     * Updates the activity status of the custact with the given no to the status of the given no.
     *
     * @datatask 1417
     * @datataskName sp_ws_p2_custact_changed_actstatus_save
     * @param params the custact no to change status for and the no of the new status.
     * @returns promise containing validation.
     */
    public async updateActivityStatus(params: UpdateActivityStatusParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(1417, params))[0];
    }

    /**
     * Retrieves page get for custact edit.
     *
     * @datatask 48
     * @datataskName sp_ws_p2_custact_get_edit
     * @param params argtype and argvalue.
     * @returns promise containing the page get.
     */
    public async getCustactEdit(params: GetCustactEditParams): Promise<CustactEditGet> {
        return (await this.datatask.Post(48, params))[0];
    }

    /**
     * Saves the given custact.
     *
     *
     * @datatask 76
     * @datataskName sp_ws_p2_custact_save
     * @param params the custact to sace.
     * @returns promise containing validation and custact no.
     */
    public async saveCustactEdit(params: SaveCustactParams): Promise<SaveCustactResponse>;
    public async saveCustactEdit(params: { state: 'update_graph_event_id'; event_id: string; custact_no: string | number }): Promise<SaveCustactResponse>;
    public async saveCustactEdit(params: SaveCustactParams | { state: 'update_graph_event_id'; event_id: string; custact_no: string | number }): Promise<SaveCustactResponse> {
        return (await this.datatask.Post(76, params))[0];
    }

    /**
     * Retrieve page get for custact dialogs for the given custact no.
     *
     * @datatask 2627
     * @datataskName sp_ws_p2_w_custactdialogs_get
     * @param params the custact no.
     * @returns promise cotnaining the page get.
     */
    public async getCustactDialogsPage(params: GetCustactParams): Promise<CustactDialogsGet> {
        return (await this.datatask.Post(2627, params))[0];
    }

    /**
     * Retrieves list of recurrence options.
     *
     * @datatask 3145
     * @datataskName tt_recurring
     * @returns promise containing list of recurrence options.
     */
    public async getRecurrenceOptions(): Promise<RecurrenceItem[]> {
        return this.datatask.Post(3145);
    }

    /**
     * Retrieves list of projects for the given cust and custact.
     *
     * @datatask 46
     * @datataskName sp_ws_p2_custact_project_list
     * @param params settings for the list.
     * @returns promise containing custact relevant projects.
     */
    public getProjects(params: GetCustactProjectsParams): Promise<CustactProjectItem[]> {
        return this.datatask.Post(46, params);
    }

    /**
     * Retrieves list of subtasks.
     *
     * @datatask 115
     * @datataskName sp_ws_p2_custact_subtasks_list
     * @param params
     * @returns promise containing list of subtasks.
     */
    public async getSubtasks(params: GetCustactSubtasksParams): Promise<CustactSubtaskItem[]> {
        return this.datatask.Post(115, params);
    }
}

export interface UpdateActivityStatusParams {
    custact_no: string | number;
    actstatus_keyno: string | number;
}

export interface GetCustactSubtasksParams {
    custact_no: string | number;
    retrievetype: 'child' | 'belongto';
    include_top: '1' | '0';
}

export interface CustactSubtaskItem {
    item_id: string;
    item_name: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
    orderby: string;
    item_state: string;
    item_parms: string;
    nbr_of_subtasks: string;
}

export interface SaveCustactParams extends CustactEditGet {
    custactproducts?: any[];
}

export interface SaveCustactResponse extends ErrorMessageResponse {
    custact_no: string;
}

export interface GetCustactProjectsParams {
    cust_no: string | number;
    custact_no: string | number;
}

export interface CustactProjectItem {
    project_keyno: string;
    project_name: string;
}

export interface RecurrenceItem {
    recurring_id: string;
    recurring_name: string;
    orderby: string;
}

export interface GetCustactEditParams {
    connectto: string;
    connectto_value: string;
    custact_no: string;
}

export interface CustactEditGet {
    custact_no: string;
    is_deviation: '1' | '0';
    datetime_start: string;
    datetime_start_label: string;
    datetime_end: string;
    datetime_end_label: string;
    cust_no: string;
    cust_name: string;
    cust_name_label: string;
    project_keyno: string;
    project_keyno_label: string;
    projectprosess_keyno: string;
    projectprosess_keyno_label: string;
    activitytype_no: string;
    activitytype_no_label: string;
    responsible_user_id: string;
    responsible_user_id_label: string;
    responsible_labour_no: string;
    responsible_labour_name: string;
    actstatus_keyno: string;
    actstatus_keyno_label: string;
    equipment_id: string;
    equipment_id_label: string;
    note: string;
    note_label: string;
    note_placeholder: string;
    note_subhelp: string;
    solution_proposal: string;
    solution_proposal_label: string;
    solution_note: string;
    solution_note_label: string;
    solution_note_preventive: string;
    solution_note_preventive_label: string;
    prod_id: string;
    prod_keyname: string;
    prod_id_label: string;
    deviation_no: string;
    deviationtype_keyno: string;
    deviationsource_keyno: string;
    deviationsource2_keyno: string;
    deviation_date: string;
    ok: '1' | '0';
    regof_labour_name: string;
    custact_no_belongto: string;
    custact_note_belongto: string;
    first_dialogue_subhelp: string;
    first_dialogue: string;
    customer_search: string;
    order_internal_no: string;
    order_order_no: string;
    show_setting: '1' | '0';
    has_deadline: '1' | '0';
    deadline: string;
    is_sales_opportunity: '1' | '0';
    sales_size_no: string;
    sales_possibility_no: string;
    furniturefabrics_id: string;
    model_id: string;
    page_heading: string;
    salarytype_id: string;
    guard: '1' | '0';
    serviceobject_keyno: string;
    serviceobject_name: string;
    projectactivitytype_id: string;
    show_projectactivitytype_id: '1' | '0';
    businessco_no: string;
    department_id: string;
    show_department_id: '1' | '0';
    show_initiated_by_person: '1' | '0';
    initiated_by_person: string;
    initiated_by_person_name: string;
    microsoft_graph_event_id: string;
    video_url: string;
    recurring: string;
    show_recurring: '1' | '0';
    show_edit_activitytype: '1' | '0';
    custtype: CustType;
}

export interface GetCustactParams {
    custact_no: string | number;
}

export interface CustactGet {
    cust_no: string;
    cust_name: string;
    cust_name_label: string;
    custact_no: string;
    note: string;
    note_label: string;
    note_placeholder: string;
    project_keyno: string;
    project_name: string;
    project_name_label: string;
    projectprosess_keyno: string;
    projectprosess_name: string;
    ok: '1' | '0';
    activitytype_name: string;
    nbr_of_powtime: string;
    sum_salaryhours: string;
    min_date_start: string;
    max_date_end: string;
    deviationtype_keyno: string;
    deviationtype_keyno_label: string;
    nbr_of_participants: string;
    nbr_of_custactdialogs: string;
    nbr_of_childs: string;
    edit_custact_possible: '1' | '0';
    solution_note: string;
    show_comments: '1' | '0';
    nbr_of_checklistdata: string;
    nbr_of_riskelements: string;
    show_riskelements: '1' | '0';
    nbr_of_documents: string;
    nbr_of_pictures: string;
    nbr_of_deviations: string;
    nbr_of_orders: string;
    order_internal_no: string;
    prepayments_badgeinfo: string;
    show_prepaymentinfo: '1' | '0';
    show_button_timeregister: '1' | '0';
    show_button_materialregister: '1' | '0';
    show_button_materialregister_hour: '1' | '0';
    show_button_deviation: '1' | '0';
    show_button_orders: '1' | '0';
    show_button_pictures: '1' | '0';
    show_button_documents: '1' | '0';
    show_button_forms: '1' | '0';
    show_button_jobcalc: '1' | '0';
    show_button_custactplanning: '1' | '0';
    show_button_subtasks: '1' | '0';
    show_button_gantt: '1' | '0';
    show_button_categoridatas: '1' | '0';
    show_button_custactportalusers: '1' | '0';
    show_button_enable_delete: '1' | '0';
    show_setting: '1' | '0';
    order_name: string;
    last_consignment_keyno: string;
    show_checkin: '1' | '0';
    nbr_of_checkedin: string;
    custactinfotext: string; //Inneholder informasjon om hvilken aktivitet denne tilhører etc,
    quantity: string;
    price_locval: string;
    price_ordval: string;
    linediscount_pst: string;
    amount_locval: string;
    amount_ordval: string;
    text_sales: string;
    text_production: string;
    sortorderno: string;
    isoption: '1' | '0';
    cust_telephone: string;
    show_cust_telephone: '1' | '0';
    drawing_no: string;
    prod_id: string;
    custactcalc_specify: '1' | '0';
    nbr_of_parents: string;
    po_keyno: string;
    orderline_keyno: string;
    custact_no_linkto: string;
    custactlink_keyno: string;
    show_goto_project: '1' | '0';
    nbr_of_custact_customer: string;
    isvisible: '1' | '0';
    show_travelbills: '1' | '0';
    nbr_of_travelbills: string;
    show_button_map: '1' | '0';
    is_sales_opportunity: '1' | '0';
    show_next_activity: '1' | '0';
    bg_workdetails_group: string;
    actstatus_keyno: string;
    activitytype_no: string;
    purch_internal_no: string;
    purch_order_no: string;
    serviceobject_keyno: string;
    serviceobject_name: string;
    show_possibleprocesses: '1' | '0';
    nbr_of_custactprocess: string;
    show_custactcomplaint: '1' | '0';
    nbr_of_custactproducts: string;
    custact_no_basis: string;
    nbr_of_used_in: string;
    bg_custactcalcedit_main: string;
    show_button_map3d: '1' | '0';
    microsoft_graph_event_id: string;
    show_send_email: '1' | '0';
    bg_tab1: string;
    webpage_heading: string;
    video_url: string;
}

export interface CustactDialogsGet {
    custact_no: string;
    note: string;
    messagetext: string;
    actstatus_keyno: string;
    activitytype_no: string;
    ok: '1' | '0';
}
